import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Layout from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { safeHtml } from "@/lib/safeHtml";
import { ArrowLeft, ArrowRight, Sparkles, BookOpen, CheckCircle2, Lightbulb, Lock } from "lucide-react";

/**
 * Public chapter reader for the "Master the 1040" preview.
 * Chapters 1-3 open for anyone; 4-13 return 402 and show a paywall.
 */
export default function Course1040Chapter() {
  const { id } = useParams();
  const chapterId = Number(id);
  const { user } = useAuth();
  const [chapter, setChapter] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    setError(null);
    api.get(`/course1040/chapter/${chapterId}`)
      .then(r => setChapter(r.data))
      .catch(e => {
        if (e?.response?.status === 402) {
          setError({ code: 402, msg: e.response.data?.detail || "This chapter is paid." });
        } else if (e?.response?.status === 404) {
          setError({ code: 404, msg: "Chapter not found." });
        } else {
          setError({ code: "err", msg: "Something went wrong loading the chapter." });
        }
      })
      .finally(() => setLoading(false));
  }, [chapterId]);

  if (loading) {
    return <Layout><div className="max-w-3xl mx-auto px-6 py-14 text-muted-foreground">Loading chapter…</div></Layout>;
  }

  if (error?.code === 402) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto px-6 py-14 text-center space-y-5" data-testid="chapter-1040-paywall">
          <Lock size={48} className="mx-auto text-primary" />
          <div className="font-serif text-4xl">Locked chapter</div>
          <p className="text-muted-foreground">
            Chapters 1–3 are always free to read. Enroll to unlock the remaining 10 chapters, all practice returns, the final exam, and your printable certificate.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Button asChild data-testid="chapter-1040-paywall-buy"><Link to="/pricing?plan=student">Enroll — $175</Link></Button>
            <Button asChild variant="outline"><Link to="/course/1040">← Back to course</Link></Button>
          </div>
        </div>
      </Layout>
    );
  }

  if (error) return <Layout><div className="max-w-3xl mx-auto px-6 py-14 text-muted-foreground">{error.msg}</div></Layout>;

  const prevId = chapterId > 1 ? chapterId - 1 : null;
  const nextId = chapterId < 13 ? chapterId + 1 : null;

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-4 md:px-6 py-8 md:py-12" data-testid={`chapter-1040-page-${chapter.id}`}>
        <div className="mb-2">
          <Link to="/course/1040" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground" data-testid="chapter-1040-back">
            <ArrowLeft size={12} /> All 1040 chapters
          </Link>
        </div>
        <div className="mb-6">
          <div className="label-caps text-primary flex items-center gap-2">
            <BookOpen size={14} /> Chapter {chapter.id} · {chapter.category}
            {chapter.free_preview && <span className="text-[10px] font-semibold bg-primary/10 text-primary rounded px-2 py-0.5 ml-2">FREE PREVIEW</span>}
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl mt-3 leading-none">{chapter.title}</h1>
          <p className="text-muted-foreground mt-3">{chapter.summary}</p>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-3">{chapter.reading_time} min read</div>
        </div>

        <article className="space-y-10">
          {chapter.sections.map((s, i) => (
            <section key={i} data-testid={`section-1040-${i}`}>
              <h2 className="font-serif text-2xl md:text-3xl mb-3">{s.heading}</h2>
              <div className="prose-tax space-y-4 text-[15px] leading-relaxed text-foreground/90">
                {s.body.split("\n\n").map((p, j) => (
                  <p key={j} dangerouslySetInnerHTML={{ __html: safeHtml(renderInline(p)) }} />
                ))}
              </div>
              {s.key_facts?.length > 0 && (
                <div className="mt-4 rounded-md border border-primary/30 bg-primary/5 p-4">
                  <div className="label-caps text-primary mb-2 flex items-center gap-1"><Sparkles size={12} /> Key facts</div>
                  <ul className="space-y-1.5 text-sm">
                    {s.key_facts.map((f, k) => (
                      <li key={k} className="flex items-start gap-2">
                        <CheckCircle2 size={14} className="flex-none text-primary mt-0.5" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </section>
          ))}
        </article>

        <div className="mt-12 pt-8 border-t border-border flex flex-wrap gap-3 justify-between">
          <Button asChild variant="outline" disabled={!prevId}>
            <Link to={prevId ? `/course/1040/chapter/${prevId}` : "#"} data-testid="chapter-1040-prev">
              <ArrowLeft size={14} className="mr-1" /> Previous
            </Link>
          </Button>
          <div className="flex gap-2">
            {chapter.free_preview && !(user && user.access_status === "active") && (
              <Button asChild data-testid="chapter-1040-enroll">
                <Link to="/pricing?plan=student">Enroll to unlock more</Link>
              </Button>
            )}
            <Button asChild disabled={!nextId}>
              <Link to={nextId ? `/course/1040/chapter/${nextId}` : "#"} data-testid="chapter-1040-next">
                Next <ArrowRight size={14} className="ml-1" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </Layout>
  );
}

function renderInline(text) {
  return String(text)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\n/g, "<br/>");
}
